// New Version bar (Robert, 2026-10-15: "there will be many revisions over the next couple of
// months when field debugging"). The iPad Home Screen app has no reload, and a new build used to
// wait until every SafeTag page was closed. Now the app looks for a new build when it opens, when
// it comes back to the front, and every five minutes. When one is ready, a gold bar shows above
// the page: "New version ready. Tap to load." One tap takes the new build.
//
// Survey saves are never caught mid-send: while any survey save is still waiting in the Outbox,
// the bar says so and does not load. Saves live on the iPad, so nothing is lost either way;
// this keeps the rule from IEA_OFFLINE_SHELL_WORDS.md section 3 (take over only when the Outbox
// is empty).
//
// The bar sits outside #app, so no page redraw removes it.

import { deviceList } from "./outbox";

export const CHECK_EVERY_MS = 5 * 60 * 1000;

/** What the bar says. Waiting survey saves hold the update. */
export function barWords(waiting: number): { text: string; canLoad: boolean } {
  if (waiting > 0) {
    return { text: `New version ready. ${waiting} survey ${waiting === 1 ? "save is" : "saves are"} still sending. It loads once the Outbox is empty.`, canLoad: false };
  }
  return { text: "New version ready. Tap to load.", canLoad: true };
}

export function barHtml(waiting: number): string {
  const w = barWords(waiting);
  return `<button class="new-version${w.canLoad ? "" : " held"}" id="new-version" type="button"${w.canLoad ? "" : " aria-disabled=\"true\""}>${w.text}</button>`;
}

async function waitingSaves(): Promise<number> {
  try {
    return (await deviceList.records()).filter((r) => r.state === "waiting").length;
  } catch {
    return 0;
  }
}

let reloading = false;
let recheck: ReturnType<typeof setTimeout> | null = null;

async function showBar(reg: ServiceWorkerRegistration): Promise<void> {
  const waiting = await waitingSaves();
  document.getElementById("new-version")?.remove();
  document.getElementById("app")?.insertAdjacentHTML("beforebegin", barHtml(waiting));
  const bar = document.getElementById("new-version");
  bar?.addEventListener("click", async () => {
    // Look again at tap time: a save may have sent, or a new one may be waiting.
    if ((await waitingSaves()) > 0) {
      void showBar(reg);
      return;
    }
    bar.textContent = "Loading the new version…";
    if (reg.waiting) reg.waiting.postMessage("takeOver");
    else location.reload();
  });
  // A held bar looks again every 20 seconds, so it turns tappable once the Outbox clears.
  if (recheck) clearTimeout(recheck);
  recheck = waiting > 0 ? setTimeout(() => void showBar(reg), 20_000) : null;
}

/**
 * Watches for a new build. Called once with the registration from registerServiceWorker.
 * A first install (no app running under a worker yet) never shows the bar.
 */
export function watchForUpdates(reg: ServiceWorkerRegistration): void {
  const sw = navigator.serviceWorker;
  sw.addEventListener("controllerchange", () => {
    if (reloading) return;
    reloading = true;
    location.reload();
  });
  const ready = () => {
    if (reg.waiting && sw.controller) void showBar(reg);
  };
  reg.addEventListener("updatefound", () => {
    const next = reg.installing;
    next?.addEventListener("statechange", () => {
      if (next.state === "installed") ready();
    });
  });
  ready();
  const check = () => void reg.update().catch(() => undefined);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  setInterval(check, CHECK_EVERY_MS);
  check();
}
